import React, { forwardRef, useState, useEffect, useRef } from "react";
import { 
    Box, TextField, Popper, Paper, List, ListItemButton, ListItemText, 
    Typography, ClickAwayListener, IconButton, Dialog, AppBar, Toolbar, Slide, useTheme, useMediaQuery
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import { TransitionProps } from '@mui/material/transitions';
import iconMap from "../../../paginas/gestao/menu/iconmap";
import tokenService from "../../../services/tokenservice";
import { useEasyBrowserContext } from "../browser";
import { MenuItemType } from "../taskbar/taskbartypes.d";

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & { children: React.ReactElement },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const FindTaskBar = forwardRef((_props: unknown, _ref: React.ForwardedRef<any>) => {
    const { abrirPagina, usuario } = useEasyBrowserContext()!;
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    
    const [allScreens, setAllScreens] = useState<MenuItemType[]>([]);
    const [results, setResults] = useState<MenuItemType[]>([]);
    const [history, setHistory] = useState<MenuItemType[]>([]);
    const [open, setOpen] = useState(false);
    const [openMobile, setOpenMobile] = useState(false);
    const [query, setQuery] = useState("");
    const containerRef = useRef<HTMLDivElement | null>(null);

    const HISTORY_KEY = `find_history_${usuario?.login || 'default'}`;

    useEffect(() => {
        const loadMenu = async () => {
            const menu = await tokenService.getMenu();
            if (menu) {
                try {
                    const parsed = JSON.parse(menu);
                    setAllScreens(parsed.menus || []);
                } catch (e) { console.error(e); }
            }
        };
        const loadHistory = () => {
            const saved = localStorage.getItem(HISTORY_KEY);
            if (saved) setHistory(JSON.parse(saved));
        };
        loadMenu();
        loadHistory();
    }, [usuario?.login]);

    useEffect(() => {
        const termo = query.trim().toLowerCase();
        if (!termo) { 
            setResults([]); 
            return; 
        }
        setResults(allScreens.filter(item => 
            item.descricao?.toLowerCase().includes(termo) || 
            item.grupo?.join(" ").toLowerCase().includes(termo)
        ));
    }, [query, allScreens]);

    const selecionar = (item: MenuItemType) => {
        const newHistory = [item, ...history.filter(h => h.id !== item.id)].slice(0, 5);
        setHistory(newHistory);
        localStorage.setItem(HISTORY_KEY, JSON.stringify(newHistory));

        abrirPagina(item);
        setQuery("");
        setOpen(false);
        setOpenMobile(false);
    };

    const renderLista = (itens: MenuItemType[], isHistory = false) => (
        <List dense={!isMobile}>
            {isHistory && itens.length > 0 && (
                <Typography variant="caption" sx={{ px: 2, py: 1, display: 'block', fontWeight: 'bold', color: 'text.secondary' }}>
                    TELAS RECENTES
                </Typography>
            )}
            {itens.map((item) => (
                <ListItemButton key={item.id} onClick={() => selecionar(item)}>
                    <Box sx={{ mr: 2, display: 'flex', color: 'action.active', '& svg': { fontSize: isMobile ? 24 : 18 } }}>
                        {isHistory ? <HistoryIcon /> : (item.icone ? iconMap[item.icone] : iconMap["default"])}
                    </Box>
                    <ListItemText 
                        primary={item.descricao} 
                        secondary={item.grupo?.join(" > ")}
                        primaryTypographyProps={{ fontSize: isMobile ? '1rem' : '0.85rem', fontWeight: 500 }}
                    />
                </ListItemButton>
            ))}
        </List>
    );

    if (isMobile) {
        return (
            <>
                <IconButton onClick={() => setOpenMobile(true)} color="primary"><SearchIcon /></IconButton>
                <Dialog fullScreen open={openMobile} onClose={() => setOpenMobile(false)} TransitionComponent={Transition}>
                    <AppBar sx={{ position: 'relative' }}>
                        <Toolbar>
                            <IconButton edge="start" color="inherit" onClick={() => setOpenMobile(false)}><CloseIcon /></IconButton>
                            <TextField
                                autoFocus
                                fullWidth
                                placeholder="Procurar tela..."
                                variant="standard"
                                autoComplete="off"
                                name={`search_screens_${Math.random()}`}
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                sx={{ ml: 2, input: { color: 'white' } }}
                            />
                        </Toolbar>
                    </AppBar>
                    <Box sx={{ bgcolor: 'background.paper', height: '100%', overflowY: 'auto' }}>
                        {query.trim() === "" ? renderLista(history, true) : renderLista(results)}
                    </Box>
                </Dialog>
            </>
        );
    }

    return (
        <ClickAwayListener onClickAway={() => setOpen(false)}>
            <Box ref={containerRef} sx={{ width: 220, bgcolor: 'rgba(0,0,0,0.04)', borderRadius: '4px', px: 1, display: 'flex', alignItems: 'center', border: '1px solid rgba(0,0,0,0.08)' }}>
                <SearchIcon sx={{ fontSize: 18, mr: 0.5, color: 'text.secondary' }} />
                <TextField
                    placeholder="Buscar (Alt+Q)"
                    variant="standard"
                    fullWidth
                    autoComplete="off"
                    name={`desktop_search_${Math.random()}`}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onFocus={() => setOpen(true)}
                    InputProps={{ disableUnderline: true, sx: { fontSize: 13, height: 32 } }}
                />
                <Popper open={open && (results.length > 0 || history.length > 0)} anchorEl={containerRef.current} placement="bottom-start" style={{ zIndex: 3000 }}>
                    <Paper elevation={8} sx={{ width: 320, maxHeight: 450, overflow: 'auto', mt: 1, borderRadius: '8px', border: `1px solid ${theme.palette.divider}` }}>
                        {query.trim() === "" ? renderLista(history, true) : renderLista(results)}
                    </Paper>
                </Popper>
            </Box>
        </ClickAwayListener>
    );
});

export default FindTaskBar;